import { TextData } from "../types";
import { formatTextLine } from "../utils";
import { RenderItem } from "../components/Chart";

/**
 * 
 * @param str label 文字
 * @param offset label 偏移量
 * @param index label 需要显示的点 [Q-EFF 会有负的导致不渲染, 需要手动操作]
 */
export const renderLabel = (
  str?: string,
  offset: number[] = [30, -5],
  index?: any,
  customStyle: Record<string, any> = {}
) => {
  if (str) {
    const tokenName = "curveLabel";
    const hasCustomStyle = Object.keys(customStyle).length > 0;

    return {
      show: true,
      formatter: (data: any) => {
        const {
          dataIndex
        } = data;

        if (dataIndex === 0 || dataIndex === index) {
          return hasCustomStyle ? `{${tokenName}|${str}}` : str;
        }

        return "";
      },
      offset,
      ...(hasCustomStyle
        ? {
            rich: {
              [tokenName]: customStyle,
            },
          }
        : customStyle),
    };
  }

  return {
    show: false
  };
};

// 自定义绘制type
export const renderTypeLabel =
  (x: number, y: number, text: string, customStyle: Record<string, any> = {}) => {
    return () => {
      return {
        type: "text",
        style: {
          text,
          lineHeight: 16,
          x,
          y,
          ...customStyle,
        },
        z2: 200,
      }
    };
  }

type RenderItemOptions = {
  textStyle?: Record<string, any>;
  panelStyle?: Record<string, any>;
  markerStyles?: {
    outer?: Record<string, any>;
    inner?: Record<string, any>;
    core?: Record<string, any>;
    ring?: Record<string, any>;
  };
  panelWidth?: number;
  panelHeight?: number;
  panelPadding?: number | number[];
  radius?: number;
  anchor?: {
    x?: number | ((size: { width: number; height: number }) => number);
    y?: number | ((size: { width: number; height: number }) => number);
  };
};

export const genRenderItem = (
  type: any,
  x?: number,
  y?: number,
  data?: TextData[],
  options: RenderItemOptions = {}
): RenderItem => {
  let text = "";
  const {
    textStyle = {},
    panelStyle = {},
    markerStyles = {},
    panelWidth = 150,
    panelHeight,
    panelPadding = [10, 12, 10, 12],
    radius = 4,
    anchor,
  } = options;

  // 仅绘制type
  if (x === undefined || y === undefined) {
    return () => {
      return {
        type
      };
    };
  }

  if (data) {
    data.forEach(d => {
      if (d.value) {
        text += formatTextLine(d.label, d.value, d.unit) + "\n";
      }
    });
  }

  // 绘制文本
  return (_param, api) => {
    if (type === "text") {
      return {
        type,
        style: {
          text,
          lineHeight: 16,
          x,
          y,
          ...textStyle,
        },
        z2: 200
      };
    }

    if (type === "textBlock") {
      const size = {
        width: api.getWidth(),
        height: api.getHeight(),
      };
      const lines = text.trim().split("\n").filter(Boolean);
      const resolvedHeight = panelHeight || Math.max(36, lines.length * 18 + 18);
      const resolvedX = typeof anchor?.x === "function" ? anchor.x(size) : (anchor?.x ?? x);
      const resolvedY = typeof anchor?.y === "function" ? anchor.y(size) : (anchor?.y ?? y);

      return {
        type: "group",
        children: [
          {
            type: "rect",
            shape: {
              x: resolvedX,
              y: resolvedY,
              width: panelWidth,
              height: resolvedHeight,
              r: radius,
            },
            style: {
              fill: "#ffffff",
              stroke: "#d9d9d9",
              lineWidth: 1,
              ...panelStyle,
            },
            silent: true,
          },
          {
            type: "text",
            style: {
              text,
              x: resolvedX + 12,
              y: resolvedY + 10,
              lineHeight: 18,
              overflow: "break",
              width: panelWidth - 24,
              padding: panelPadding,
              ...textStyle,
            },
            silent: true,
          },
        ],
        z2: 200,
      };
    }

    const [cx, cy] = api.coord([x, y]); // 将数据值映射到坐标系上

    if (type === 'circle1') {
      return {
        type: 'circle',
        shape: { cx, cy, r: 5 },
        style: { fill: "#1890ff", ...markerStyles.outer },
        z2: 200
      }
    }

    if (type === 'circle2') {
      return {
        type: 'circle',
        shape: { cx, cy, r: 3.5 },
        style: { fill: "#ffffff", ...markerStyles.inner },
        z2: 201
      }
    }

    if (type === "circle3" || type === "circle") {
      return {
        type: "circle",
        shape: { cx, cy, r: 4.5 },
        style: {
          fill: "#ffffff",
          stroke: "#1890ff",
          lineWidth: 1.8,
          ...markerStyles.ring,
        },
        z2: 202,
      };
    }

    // 绘制交点
    return {
      type: 'circle',
      shape: { cx, cy, r: 2 },
      style: { fill: "#1890ff", ...markerStyles.core },
      z2: 202
    }
  };
};

export const exportTemplate = {
  type: "png",
  pixelRatio: 2,
  backgroundColor: "#fff",
  excludeComponents: ["toolbox"],
};
